import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-konva';
import Konva from 'konva';
import { FACET_HIGHLIGHT_COLOR, KONVA_FACET_GROUP } from '../../../../constants';
import { KonvaGroupNames } from '../../utils';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { redoEditableFacets, setEditableFacets, undoEditableFacets } from 'store/slices/RoofEditSlice';
import { useRefs } from 'contexts/RefContext';
import { addNewPoint, distanceToEdge, roundPoint } from '../utils';
import { CLICK_ON_LINE_THRESHOLD, EDITED_FACET_HIGHLIGHT_COLOR, INVAILD_FACET_HIGHLIGHT } from '../constant';
import { getMergeableFacetIds } from './mergeFacets';
import useMergeFacets from './useMergeFacets';
import { appToast } from 'components/Basic/CustomToast';

// distinct from the edited-facet blue, so a merge selection is recognizable
const MERGE_SELECTED_FACET_COLOR = 'rgba(16, 185, 129, 0.65)';
const MERGE_SELECTED_FACET_STROKE = 'rgba(16, 185, 129, 0.65)';
// fixed id so repeated taps replace the toast instead of stacking them
const NOT_MERGEABLE_TOAST_ID = 'facet-not-mergeable';

interface Props extends Konva.LineConfig {
	segmentId: string;
}

const EditableFacet = (props: Props) => {
	const { segmentId } = props;
	const { editableFacets, invalidFacetIDs, enableMergeFacets, mergeSelectedFacetIds } =
		useSelector((state: RootState) => state.roofEditData.data);
	const { konvaRef } = useRefs();
	const currentSegment = editableFacets.find(eachSeg => eachSeg.segmentId === segmentId);
	const [points, setPoints] = useState(currentSegment?.coordinates.flat() || []);
	const scale = useKonvaImageScale();
	const dispatch = useDispatch();
	const mergeFacetOnClick = useMergeFacets();

	useEffect(() => {
		if (currentSegment) {
			setPoints(currentSegment.coordinates.flat());
		}
	}, [currentSegment]);

	const isInvalid = currentSegment
		? invalidFacetIDs.includes(currentSegment.segmentId)
		: false;

	// a merge result is a single facet like any other, so it keeps the merge-selected
	// look and can be clicked again to unselect
	const isSelectedForMerge = mergeSelectedFacetIds.includes(segmentId);

	// only facets sharing an edge with the current selection can be picked next
	const mergeableFacetIds = useMemo(() => (
		enableMergeFacets ? getMergeableFacetIds(mergeSelectedFacetIds, editableFacets) : []
	), [editableFacets, enableMergeFacets, mergeSelectedFacetIds]);

	const isMergeable = isSelectedForMerge || mergeableFacetIds.includes(segmentId);

	const transformedPoints = useMemo(() => (
		points.map((point, index) =>
			index % 2 === 0 ? point * scale.x : point * scale.y
		)), [points, scale.x, scale.y]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const key = event.key.toLowerCase();
			if ((event.ctrlKey || event.metaKey) && key === 'z' && !event.shiftKey) {
				dispatch(undoEditableFacets());
			} else if ((event.ctrlKey || event.metaKey) && (key === 'y' || (key === 'z' && event.shiftKey))) {
				dispatch(redoEditableFacets());
			}
		};
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [dispatch, editableFacets]);

	const handleMergeClick = (e: Konva.KonvaEventObject<MouseEvent | Event>) => {
		e.cancelBubble = true;
		if (e.evt instanceof MouseEvent && e.evt.button === 2) return;
		if (!isMergeable) {
			// touch devices have no hover cursor to signal a facet cannot be merged
			if (e.evt.type.startsWith('touch')) {
				appToast.warn('Selected facets cannot be merged.', { toastId: NOT_MERGEABLE_TOAST_ID });
			}
			return;
		}

		mergeFacetOnClick(segmentId);
	};

	const handleMergeHover = (e: Konva.KonvaEventObject<MouseEvent>, event: 'ENTER' | 'OUT') => {
		const container = e.target?.getStage()?.container();
		if (!container) return;
		container.style.cursor = event === 'OUT' ? 'default' : (isMergeable ? 'pointer' : 'not-allowed');
	};

	const handlePointerClick = (e: Konva.KonvaEventObject<MouseEvent | Event>) => {
		if (enableMergeFacets) return;
		const absPointerPos = konvaRef?.current?.getRelativePointerPosition();

		if (!absPointerPos || (e.evt instanceof MouseEvent && e.evt.button === 2)) return;
		
		const facetsHit: string[] = [];
		const facetLines = konvaRef?.current?.find(
			`.editable-${KonvaGroupNames.existingFacetsGroup}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}facet`
		) as Konva.Line[];

		facetLines.forEach((line) => {
			const id = line.id();
			const points = line.points();
			const dist = distanceToEdge(absPointerPos, points);
			if (dist < CLICK_ON_LINE_THRESHOLD) {
				facetsHit.push(id.replace('editable-',''));
			}
		});

		if(facetsHit.length <= 0) return;

		const updatedFacets = editableFacets.map(facet => {
			if (facetsHit.includes(facet.segmentId)) {
				const [x,y] = roundPoint([absPointerPos.x, absPointerPos.y]);
				const newCoords = addNewPoint({x,y}, facet.coordinates.map(p => [...p]), scale);
				return {
					...facet,
					coordinates: newCoords,
					isEdited: true,
					isSelected: true,
				};
			}
			return { ...facet, isSelected: false };
		});

		dispatch(setEditableFacets({ editableFacets: updatedFacets }));
	};

	// any facet in the merge selection (merge results included) gets the merge highlight;
	// everything else keeps the normal edited / highlight colors
	const facetFill = (() => {
		if (isInvalid) return INVAILD_FACET_HIGHLIGHT;
		if (enableMergeFacets && isSelectedForMerge) return MERGE_SELECTED_FACET_COLOR;
		return currentSegment?.isEdited ? EDITED_FACET_HIGHLIGHT_COLOR : FACET_HIGHLIGHT_COLOR;
	})();

	const facetStroke = (() => {
		if (isInvalid) return INVAILD_FACET_HIGHLIGHT;
		if (enableMergeFacets && isSelectedForMerge) return MERGE_SELECTED_FACET_STROKE;
		return 'orange';
	})();

	if (!points.length || !currentSegment) return null;

	return (
		<Line
			name={`editable-${KonvaGroupNames.existingFacetsGroup}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}facet`}
			id={`editable-${currentSegment.segmentId}`}
			points={transformedPoints}
			valid={!isInvalid}
			draggable={false}
			fill={facetFill}
			stroke={facetStroke}
			strokeWidth={2}
			hitStrokeWidth={8}
			strokeScaleEnabled={false}
			closed={true}
			onClick={enableMergeFacets ? handleMergeClick : handlePointerClick}
			onTap={enableMergeFacets ? handleMergeClick : handlePointerClick}
			onMouseEnter={enableMergeFacets ? (e) => handleMergeHover(e, 'ENTER') : undefined}
			onMouseLeave={enableMergeFacets ? (e) => handleMergeHover(e, 'OUT') : undefined}
			listening={true}
		/>);
			
};

export default EditableFacet;